import React, { useState, useRef } from 'react'
import {
  Popover,
  createStyles,
  withStyles,
  useClientOptions,
  useUser,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import EditIcon from '@material-ui/icons/Edit'
import type { QuoteDocument } from 'paintscout'
import type { LabelsOption } from '@paintscout/util/builder'
import { getTerms, updateTerms } from '@paintscout/util/builder'
import { isQuoteEditable } from '@paintscout/util/util'
import EditTermsForm from './EditTermsForm'

export interface EditTermsDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  quote: QuoteDocument
  editable: boolean
  permissionToEdit?: boolean
  hasActiveAccount?: boolean
  objectLabels: LabelsOption
  onToggleEdit?: (editStatus: boolean) => void
  onConfirm: (quote: QuoteDocument, isDirty: boolean) => void
  onCancel: () => void
}

export interface EditTermsDialogState {
  quote: QuoteDocument
  isDirty: boolean
}

const styles = (_theme: Theme) => {
  return createStyles({
    root: {},
    content: {
      overflowX: 'hidden'
    }
  })
}

function EditTermsDialog({
  onConfirm,
  onCancel,
  onToggleEdit,
  classes,
  editable,
  permissionToEdit,
  objectLabels,
  quote,
  ...otherProps
}: EditTermsDialogProps) {
  const { options } = useClientOptions()
  const { trialExpired } = useUser()

  const originalTerms = getTerms({ quote, options })
  const [editToggleAnchor, setEditToggleAnchor] = useState<HTMLElement | null>(null)
  const [targetPos, setTargetPos] = useState<{ top: number; left: number } | null>(null)
  const [isEditing, setIsEditing] = useState(editable)
  const isTemplate = quote?.smartTemplate && quote.type === 'quoteTemplate'
  const isEditable = permissionToEdit && (isQuoteEditable(quote) || isEditing) && !trialExpired && !isTemplate
  const contentRef = useRef(null)
  const termsRef = useRef(null)

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditable && !trialExpired && permissionToEdit && !isTemplate) {
      setEditToggleAnchor(event.currentTarget)
      setTargetPos({ top: event.clientY, left: event.clientX })
    }
  }

  const handleClose = () => {
    setEditToggleAnchor(null)
  }

  const leftButton = (
    <Button onClick={onCancel} variant="textNoPadding">
      Cancel
    </Button>
  )

  return (
    <Dialog className={classes.root} {...otherProps} fullWidth={true} maxWidth={'lg'}>
      <DialogTitle>{'Edit Terms & Conditions'}</DialogTitle>
      <DialogContent className={classes.content}>
        <Popover
          open={!!editToggleAnchor}
          anchorReference="anchorPosition"
          anchorEl={editToggleAnchor}
          anchorPosition={targetPos}
          onClose={handleClose}
        >
          <MenuItem
            icon={EditIcon}
            onClick={(ev) => {
              ev.stopPropagation()
              setIsEditing(true)
              onToggleEdit(true)
              setEditToggleAnchor(null)
            }}
          >
            {`Edit ${objectLabels.quote.value}`}
          </MenuItem>
        </Popover>
        <div onClick={handleClick}>
          <EditTermsForm termsRef={termsRef} contentRef={contentRef} editable={isEditable} />
        </div>
      </DialogContent>
      <DialogActions showBorder={true} leftButton={leftButton}>
        {isEditable ? (
          <Button onClick={handleConfirm} variant={'contained'}>
            Done
          </Button>
        ) : (
          <Button onClick={onCancel} variant={'contained'}>
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )

  function handleConfirm(_event: any) {
    const updatedTermsContent = contentRef.current?.getHTML()
    const currentTerms = termsRef.current
    const isCustom = currentTerms.content !== updatedTermsContent
    const termsChanged = originalTerms.name !== currentTerms.name
    const isDirty = isCustom || termsChanged

    if (isDirty) {
      onConfirm(
        updateTerms({
          quote,
          terms: {
            ...currentTerms,
            name: `${isCustom ? 'custom-' + currentTerms.name : currentTerms.name}`,
            content: updatedTermsContent,
            custom: isCustom
          }
        }),
        isDirty
      )
    } else {
      onCancel()
    }
  }
}

export default withStyles(styles)(EditTermsDialog)
